/**
 * The menu bar lines that belong to the app itself rather than to one service: Help (the
 * keyboard shortcuts, about Knodal) and copying the graph as JSON. Every other line comes from
 * the service it acts on (`menus` in `subsystems.ts`).
 */
import type { App } from "./context";
import type { MenuSection } from "../shared/menus";
import { toast } from "../shared/toast";
import { copyText } from "../shared/ui";

/** What the built-in commands are called in the shortcuts list. */
const COMMAND_NAMES: Record<string, string> = {
  "project.save": "Save the project",
  "graph.undo": "Undo",
  "graph.redo": "Redo",
  "selection.delete": "Delete the selection",
  "selection.clear": "Clear the selection",
  "tool.link": "Link tool",
  "view.fit": "Fit the view",
  "view.3d": "2D / 3D",
  "view.diagram": "Nodal view / class diagram",
  "search.focus": "Find a node",
  "theme.cycle": "Next theme",
  "layout.reset": "Reset the layout",
  "window.toggle": "Open or close a window",
  "window.open": "Open a window",
  "space.leave": "Leave the space",
  "presets.drawer": "Presets drawer",
  "node.add": "Add a node",
  "group.fold": "Fold or unfold the group",
  "group.rename": "Rename the group",
  "palette.open": "Find an action",
  "environment.use": "Work in an environment",
  "workspace.use": "Work in a workspace of the environment",
};

/** The app's own menu sections. */
export function shellMenus(app: App): MenuSection[] {
  /** Copies the whole graph as JSON to the clipboard. */
  const copyJson = async () => {
    const text = JSON.stringify(app.graph.state.graph, null, 2);
    toast((await copyText(text)) ? "Graph JSON copied." : "Copy is blocked here.");
  };
  /** Every shortcut in use, as lines that also run it. */
  const shortcuts = () =>
    app.keys.binds().map((b) => ({
      label: `${COMMAND_NAMES[b.action] ?? b.action}${b.argument ? ` (${b.argument})` : ""}`,
      command: b.action,
      argument: b.argument ?? null,
    }));
  return [
    { menu: "edit", section: "Graph", order: 20, entries: () => [{ label: "Copy the graph as JSON", action: () => void copyJson() }] },
    {
      menu: "help",
      section: "Help",
      order: 10,
      entries: () => [
        { label: "Keyboard shortcuts", items: shortcuts() },
        { label: "About Knodal", action: () => toast("Knodal: think in systems. Design an app's structure, alone or together.") },
      ],
    },
  ];
}
